import { request } from "../client";
import { saveLogin } from "../session";
import type { User, Tenant } from "../types";
import { hubBody, hubRequest } from "./client";
import type { Actor, Person, PlatformSettings, Role } from "./types";
export const getMe = () => hubRequest<Actor>("/me");
export const listPeople = () => hubRequest<Person[]>("/people");
const settingsForUi = (s: PlatformSettings): PlatformSettings => ({
  ...s,
  notifications: {
    focusTime: String(s.focusTime ?? s.notifications?.focusTime ?? "09:00"),
    summaryTime: String(
      s.summaryTime ?? s.notifications?.summaryTime ?? "18:30",
    ),
    weeklyTime: String(s.weeklyTime ?? s.notifications?.weeklyTime ?? "09:30"),
  },
  ai: {
    enabled: s.aiEnabled !== false,
    mode: (s.aiMode ?? s.ai?.mode ?? "automatic") as "automatic" | "suggestion",
    actions: (s.aiActions ?? s.ai?.actions ?? {}) as Record<string, boolean>,
  },
});
export const getSettings = () =>
  hubRequest<PlatformSettings>("/settings").then(settingsForUi);
export function updateSettings(settings: PlatformSettings) {
  const { notifications, ai, ...flat } = settings;
  const body = {
    ...flat,
    ...(notifications
      ? {
          focusTime: notifications.focusTime,
          summaryTime: notifications.summaryTime,
          weeklyTime: notifications.weeklyTime,
        }
      : {}),
    ...(ai
      ? {
          aiEnabled: ai.enabled !== false,
          aiMode: ai.mode ?? "automatic",
          aiActions: ai.actions ?? {},
        }
      : {}),
  };
  return hubRequest<PlatformSettings>("/settings", hubBody(body, "PUT")).then(
    settingsForUi,
  );
}
export const updateRole = (id: string, role: Role) =>
  hubRequest<Person>(
    `/people/${encodeURIComponent(id)}/role`,
    hubBody({ role }, "PATCH"),
  );
export type FeishuLoginResult = {
  token?: string;
  weknoraToken?: string;
  refresh_token?: string;
  refreshToken?: string;
  actor?: Actor;
  user?: User;
  tenant?: Tenant;
  bindingRequired?: boolean;
  ticket?: string;
  authorizationUrl?: string;
};
export const prepareFeishuAccess = () =>
  request<{ data: { appId: string; state: string; scopeList: string[] } }>(
    "/hub/feishu/auto-login",
  ).then((r) => r.data);
export const prepareFeishuLogin = () =>
  request<{ data: { authorizationUrl: string; state: string } }>(
    "/hub/feishu/login",
    { query: { format: "json" } },
  ).then((r) => r.data);
export const feishuAccess = (code: string, state: string) =>
  request<{ data: FeishuLoginResult }>("/hub/feishu/auto-login", {
    body: { code, state },
  }).then((r) => r.data);
export const bindFeishu = (ticket: string, email?: string, password?: string) =>
  request<{ data: FeishuLoginResult }>("/hub/feishu/bind", {
    body: { ticket, mode: email ? "existing" : "auto", email, password },
  }).then((r) => r.data);
const FEISHU_LOGIN_HOSTS = new Set([
  "accounts.feishu.cn",
  "open.feishu.cn",
  "passport.feishu.cn",
]);
const LOGIN_STARTED_KEY = "lingzhi_feishu_login_started";
const BIND_EXISTING_KEY = "lingzhi_feishu_bind_existing";
const LOGIN_RESULT_TTL_MS = 15 * 60_000;
/** 只有用户明确选择绑定已有账号时才记录；读取失败时应拒绝发起该分支。 */
export function rememberExistingFeishuAccount() {
  try {
    sessionStorage.setItem(BIND_EXISTING_KEY, "1");
  } catch {
    throw new Error("浏览器无法保存绑定选择，请使用知识库账号登录");
  }
}
export function consumeExistingFeishuAccount() {
  try {
    const existing = sessionStorage.getItem(BIND_EXISTING_KEY) === "1";
    sessionStorage.removeItem(BIND_EXISTING_KEY);
    return existing;
  } catch {
    return false;
  }
}
/** 登录只允许跳到本站或飞书官方授权页，挡住 javascript:、data: 和外站地址。 */
export function trustedLoginRedirect(raw: string) {
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    throw new Error("登录跳转地址无效，请重新登录");
  }
  const sameOrigin = url.origin === window.location.origin;
  const feishu = url.protocol === "https:" && FEISHU_LOGIN_HOSTS.has(url.hostname);
  if (!sameOrigin && !feishu) throw new Error("登录跳转地址不可信，请重新登录");
  return url.toString();
}
export function markFeishuLoginStarted() {
  try {
    sessionStorage.setItem(LOGIN_STARTED_KEY, String(Date.now()));
  } catch {
    // 无痕模式等禁用存储时，回来的登录结果会被拒绝，用户可改用账号登录。
  }
}
/** 地址片段里的登录结果，只在本标签页刚发起过飞书登录时才接受，防止别人用构造的链接把他的账号塞进来。 */
export function consumeFeishuLoginStarted() {
  let started = 0;
  try {
    started = Number(sessionStorage.getItem(LOGIN_STARTED_KEY) || 0);
    sessionStorage.removeItem(LOGIN_STARTED_KEY);
  } catch {
    return false;
  }
  const age = Date.now() - started;
  return started > 0 && age >= 0 && age < LOGIN_RESULT_TTL_MS;
}
export function acceptFeishuLogin(r: FeishuLoginResult) {
  if (r.authorizationUrl) {
    window.location.assign(trustedLoginRedirect(r.authorizationUrl));
    return false;
  }
  const token = r.weknoraToken;
  if (!token) throw new Error("需要知识库授权才能继续，请使用飞书登录入口");
  const user =
    r.user ??
    ({
      id: r.actor?.id ?? "",
      username: r.actor?.name ?? "成员",
      email: "",
      tenant_id: Number(r.actor?.tenantId ?? 10000),
    } as User);
  saveLogin({ token, refreshToken: r.refreshToken ?? r.refresh_token, user, tenant: r.tenant });
  return true;
}
export type ScopedKey = {
  id: string;
  name: string;
  scope?: string;
  scopes?: string[];
  revokedAt?: number | null;
  createdAt?: number;
  key?: string;
};
export const listScopedKeys = () => hubRequest<ScopedKey[]>("/keys");
export const createScopedKey = (name: string) =>
  hubRequest<ScopedKey>("/keys", hubBody({ name, scopes: ["posts:create", "people:read"] }));
export const revokeScopedKey = (id: string) =>
  hubRequest<{ revoked: boolean }>(
    `/keys/${encodeURIComponent(id)}`,
    hubBody(undefined, "DELETE"),
  );
